import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchAllCommercePartners } from "../../../../redux/commercePartnersSlice";

import defaultLogo from "../../../../assets/meta/logo.png";

const GeneralCommercePartners: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { partners, loading } = useSelector(
    (state: RootState) => state.commercePartners
  );

  const allUsers = useSelector(
    (state: RootState) => state.auth.allUsers
  );

  const [animatedCount, setAnimatedCount] = useState(0);

  useEffect(() => {
    dispatch(fetchAllCommercePartners());
  }, [dispatch]);

  /* 🔢 Animated Counter */
  useEffect(() => {
    let start = 0;
    const end = partners.length;
    if (end === 0) {
      setAnimatedCount(0);
      return;
    }

    const duration = 800;
    const stepTime = Math.max(Math.floor(duration / end), 20);

    const timer = setInterval(() => {
      start += 1;
      setAnimatedCount(start);
      if (start >= end) clearInterval(timer);
    }, stepTime);

    return () => clearInterval(timer);
  }, [partners.length]);

  const handleReview = (brandId: string) => {
    navigate(`/GeneralCommercePartnersReview/${brandId}`);
  };

  if (loading)
    return <div style={styles.loading}>Loading commerce partners...</div>;

  return (
    <div style={styles.wrapper}>
      
      {/* 📊 STATS CARD */}
      <div style={styles.statsCard}>
        <div style={styles.statsGrid}>
          <div style={styles.graphWrap}>
            <svg width="100%" height="120" viewBox="0 0 300 120">
              <motion.path
                d={`M0 100 Q75 ${100 - partners.length * 3} 
                    150 ${90 - partners.length * 2} 
                    T300 ${80 - partners.length}`}
                fill="none"
                stroke="#0A66C2"
                strokeWidth="4"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1 }}
              />
            </svg>
          </div>

          <div style={styles.numberWrap}>
            <div style={styles.bigNumber}>{animatedCount}</div>
            <div style={styles.numberLabel}>Total Partners</div>
          </div>
        </div>
      </div>

      {partners.length === 0 && (
        <div style={{ marginTop: 40, opacity: 0.7 }}>
          No commerce partners found.
        </div>
      )}

      {/* 🧾 Partner Cards */}
      <div style={styles.grid}>
        {partners.map((partner) => {
          const user = allUsers.find(
            (u) => u.userId === partner.userId
          );

          const fullName = user
            ? `${user.firstName || ""} ${user.middleName || ""} ${user.lastName || ""}`.trim()
            : "Unknown User";

          const initials = user
            ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`
            : "U";

          const isVerified = user?.verifyIdentity || false;

          return (
            <div
              key={partner.brandId}
              style={styles.card}
              onClick={() => handleReview(partner.brandId)}
            >
              {/* Brand Logo */}
              <div style={styles.imageOuter}>
                <div style={styles.imageInner}>
                  <img
                    src={partner.image || defaultLogo}
                    alt={partner.name}
                    style={styles.image}
                  />
                </div>
              </div>

              {/* Info */}
              <div style={styles.info}>
                <h3 style={styles.name}>
                  {partner.name || "Untitled Brand"}
                </h3>

                <p style={styles.category}>
                  {partner.category || "No category"}
                </p>

                <button style={styles.button}>
                  Review Partner
                </button>
              </div>

              {/* 👤 USER SECTION */}
              <div style={styles.userSection}>
                <div style={styles.avatarWrapper}>
                  {user?.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt={fullName}
                      style={styles.avatar}
                    />
                  ) : (
                    <div style={styles.initialAvatar}>
                      {initials}
                    </div>
                  )}

                  <div style={styles.badge}>
                    {isVerified ? (
                      <CheckCircleIcon
                        style={{ color: "#00C853", fontSize: 22 }}
                      />
                    ) : (
                      <CancelIcon
                        style={{ color: "#D32F2F", fontSize: 22 }}
                      />
                    )}
                  </div>
                </div>

                <div style={styles.userName}>
                  {fullName}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GeneralCommercePartners;

/* ------------------- STYLES ------------------- */

const styles: { [key: string]: React.CSSProperties } = {

  wrapper: {
    padding: "40px 24px",
    maxWidth: 1200,
    margin: "0 auto",
  },

  statsCard: {
    background: "#ffffff",
    borderRadius: 20,
    padding: 30,
    marginBottom: 50,
    boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 30,
    alignItems: "center",
  },

  graphWrap: { height: 120 },

  numberWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },

  bigNumber: {
    fontSize: 64,
    fontWeight: 900,
    color: "#0A66C2",
  },

  numberLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: "#444",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
    gap: 30,
  },

  card: {
    display: "flex",
    alignItems: "center",
    background: "#111",
    borderRadius: 18,
    padding: 20,
    gap: 20,
    cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.6)",
  },

  imageOuter: {
    padding: 10,
    background: "#1a1a1a",
    borderRadius: 16,
  },

  imageInner: {
    width: 90,
    height: 90,
    borderRadius: 14,
    overflow: "hidden",
    background: "#000",
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  info: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },

  name: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 6,
    color: "#fff",
  },

  category: {
    fontSize: 14,
    opacity: 0.7,
    marginBottom: 14,
    color: "#ccc",
  },

  button: {
    padding: "8px 18px",
    borderRadius: 8,
    background: "#0A66C2",
    border: "none",
    fontWeight: 600,
    color: "#fff",
  },

  userSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    minWidth: 120,
  },

  avatarWrapper: {
    position: "relative",
    width: 70,
    height: 70,
  },

  avatar: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #fff",
  },

  initialAvatar: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    background: "#0A66C2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: 20,
  },

  badge: {
    position: "absolute",
    bottom: -2,
    right: -2,
    background: "#111",
    borderRadius: "50%",
  },

  userName: {
    fontSize: 13,
    fontWeight: 600,
    textAlign: "center",
    color: "#fff",
    maxWidth: 120,
  },

  loading: {
    minHeight: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
};
